import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Trash2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from '@/components/ui/drawer';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAIChat } from '@/hooks/useAIChat';
import { ChatMessage } from './ChatMessage';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';

export const ChatCopilot = () => {
  const {
    messages,
    isStreaming,
    isOpen,
    sendMessage,
    clearMessages,
    cancelStream,
    toggleOpen,
    closeChat,
  } = useAIChat();

  const [inputValue, setInputValue] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages]);

  const handleSend = () => {
    if (!inputValue.trim() || isStreaming) return;

    const context = {
      currentPage: location.pathname,
    };

    sendMessage(inputValue, context);
    setInputValue('');
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const quickActions = [
    { label: '📅 Plan this week\'s menu', prompt: 'Help me plan a balanced, kid-friendly menu for this week' },
    { label: '🍳 Recipe ideas', prompt: 'Suggest some easy camp recipes using what I have in stock' },
    { label: '📦 What needs reordering?', prompt: 'What ingredients am I running low on that I should reorder?' },
  ];

  return (
    <>
      {/* Floating Button */}
      <Button
        onClick={toggleOpen}
        className={cn(
          "fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-2xl z-50",
          "bg-copper hover:bg-copper-dark transition-all duration-300",
          "border-2 border-copper-light",
          isOpen && "scale-0 opacity-0"
        )}
        aria-label="Open AI Chat"
      >
        <MessageCircle className="w-6 h-6 text-sand" />
      </Button>

      {/* Chat Drawer */}
      <Drawer open={isOpen} onOpenChange={toggleOpen}>
        <DrawerContent className="h-[85vh] max-h-[700px] bg-sand border-deep-teal">
          <DrawerHeader className="border-b border-border bg-midnight-green text-ivory">
            <div className="flex items-center justify-between">
              <DrawerTitle className="text-lg font-semibold">AI Assistant</DrawerTitle>
              <div className="flex items-center gap-2">
                {messages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearMessages}
                    className="text-ivory hover:text-copper hover:bg-deep-teal"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <DrawerClose asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-ivory hover:text-copper hover:bg-deep-teal"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </DrawerClose>
              </div>
            </div>
          </DrawerHeader>

          <div className="flex flex-col h-full">
            {/* Messages Area */}
            <ScrollArea ref={scrollAreaRef} className="flex-1 p-4">
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-6">
                  <MessageCircle className="w-12 h-12 text-copper mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    How can I help you today?
                  </h3>
                  <p className="text-sm text-muted-foreground mb-6">
                    Ask me about recipes, menus, ingredients, or orders
                  </p>
                  
                  <div className="flex flex-col gap-2 w-full max-w-sm">
                    {quickActions.map((action, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setInputValue(action.prompt);
                          textareaRef.current?.focus();
                        }}
                        className="justify-start text-left hover:bg-copper/10 hover:border-copper"
                      >
                        {action.label}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, idx) => (
                    <ChatMessage
                      key={idx}
                      role={message.role}
                      content={message.content}
                      timestamp={message.timestamp}
                    />
                  ))}
                  {isStreaming && messages[messages.length - 1]?.role === 'user' && (
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-deep-teal flex items-center justify-center">
                        <Loader2 className="w-4 h-4 text-ivory animate-spin" />
                      </div>
                      <div className="text-sm text-muted-foreground">Thinking...</div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input Area */}
            <div className="border-t border-border p-4 bg-background">
              <div className="flex gap-2 items-end">
                <Textarea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything..."
                  className="min-h-[44px] max-h-[120px] resize-none"
                  disabled={isStreaming}
                />
                {isStreaming ? (
                  <Button
                    onClick={cancelStream}
                    variant="outline"
                    size="icon"
                    className="flex-shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSend}
                    disabled={!inputValue.trim()}
                    size="icon"
                    className="flex-shrink-0 bg-copper hover:bg-copper-dark text-sand"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};
